import { FC, useMemo } from 'react'
import PDModal from '../pd-modal'
import { useTranslations } from 'next-intl'
import { Form, Formik, Field, ErrorMessage, useFormikContext, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { submitCommonHubspot } from '@/service/form.service'
import toast, { Toaster } from 'react-hot-toast'
import { buildCanonicalFormNameByWindowHref } from '@/common/uri-helper'
import { EventReportManager } from '@/event-report/event-report-manager'
import { useFetchUTMParam } from '@/hooks/use-fetch-utm-param'
import { sha256 } from '@/common/misc'

const FeedbackForm = () => {
  const t = useTranslations()
  const { errors, isSubmitting } = useFormikContext()

  const disabled = useMemo(() => {
    return isSubmitting || Object.keys(errors).length > 0
  }, [errors, isSubmitting])

  return (
    <Form className="flex flex-col w-full gap-y-6">
      <div className="flex flex-col gap-y-2">
        <label htmlFor="email" className="flex items-center">
          <img src="/icons/icon_required.svg" />
          <span className="text-black/90 text-base font-semibold">{t('Modal.Email')}</span>
        </label>
        <Field
          type="text"
          name="email"
          className="h-[52px] bg-pdbg rounded-tl rounded-bl border-none"
          placeholder={t('Modal.EmailPlaceholder')}
        />
        <ErrorMessage name="email" render={(errorMessage) => <p className="text-xs text-red-500">{errorMessage}</p>} />
      </div>
      <div className="flex flex-col gap-y-2">
        <label htmlFor="message" className="flex items-center">
          <img src="/icons/icon_required.svg" />
          <span className="text-black/90 text-base font-semibold">{t('Modal.Message')}</span>
        </label>
        <Field name="message" as="textarea" className="bg-pdbg border-none h-163px" placeholder={t('Modal.Message')} />
        <ErrorMessage
          name="message"
          render={(errorMessage) => <p className="text-xs text-red-500">{errorMessage}</p>}
        />
      </div>
      <button
        type="submit"
        disabled={disabled}
        className={clsx(
          'w-44 h-12 rounded-[34px] text-base font-semibold self-center text-white',
          disabled ? 'bg-black/30' : 'bg-pdblue hover:bg-blue-600 transition-colors'
        )}
      >
        {t('Submit')}
      </button>
    </Form>
  )
}

interface FeedbackModalProps {
  show: boolean
  onClose: () => void
}

interface FormValues {
  email: string
  message: string
}

const FeedbackModal: FC<FeedbackModalProps> = ({ show, onClose }) => {
  const t = useTranslations()
  const { fetchUTMParam } = useFetchUTMParam()

  const schema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .required(t('FormError.Required'))
      .matches(/.*?@(?!temporary\-mail.net)/gi, 'Please fill in the correct email address'),
    message: Yup.string().trim().required(t('FormError.Required')),
  })

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    submitCommonHubspot({ data: { web_feedback_message__c: values.message }, email: values.email })
      .then(async (res) => {
        if (res.code === 200 || res.code === 0) {
          const sha256Email = await sha256(values.email)
          const evt = {
            data: {
              event: 'generate_feedback',
              email: values.email,
              sha256_email: sha256Email,
            },
          }
          EventReportManager.getInstance().fireEventImmediately(evt, true)
          toast.success(t('Modal.SubmitSuccess'), {
            style: {
              backgroundColor: '#dcfce7',
            },
          })
          onClose()
        } else {
          const error = res.message
          toast.error(error)
        }
      })
      .catch((res) => {
        const response = res.response?.data
        const error = response?.message
        toast.error(error)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <PDModal show={show} onClose={onClose}>
      <Toaster />
      <p className="text-black/90 text-sm font-normal leading-snug">{t('Modal.Feedback')}</p>
      <Formik
        initialValues={{ email: '', message: '' }}
        validationSchema={schema}
        onSubmit={handleSubmit}
        validateOnMount
      >
        <FeedbackForm />
      </Formik>
    </PDModal>
  )
}
export default FeedbackModal
